import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { Checkbox, Col, Row, Form, FormInstance, Divider } from 'antd';
import { datetime } from '@common/services/datetime';
import { OrganizationSettingQuery } from 'works/legacy/graphql/organization-setting';
import { useQuery } from '@common/hooks';
import {
  DatePicker,
  ErrorHandler,
  LoadingState,
  InfoTooltip,
  Text,
  Title,
  Gutter,
} from '@common/components';
import { DYNAMIC_RATES_INPUT_KEYS } from '@scheduling/services';
import { isPastDay } from 'works/legacy/lib/date-helper';
import { UnitRateType } from '@scheduling/graphql';
import { RateCurrencyInput } from './_rate-currency-input';
import { filterFormData } from './_incentives-update-steps-service';
import { VariableRatesInputsTable } from './_variable-rates-inputs-table';
import {
  StaticPricingTiersFormList,
  StaticPricingTierFormValue,
  DEFAULT_STATIC_PRICING_TIERS,
} from './_static-pricing-tiers-form-list';
import { FontSize, LineHeight } from '@common/tokens';

const [priceRange, incentiveIncrement] = DYNAMIC_RATES_INPUT_KEYS;

const STATIC_INITIAL_VALUES = {
  displayAsLumpSum: true,
  staticPricingTiers: DEFAULT_STATIC_PRICING_TIERS,
};

function LabelWithTooltip({
  label,
  tooltipText,
}: {
  label: string;
  tooltipText: string;
}) {
  return (
    <>
      <span>{label}</span>
      <InfoTooltip size="sm" leftOffset="lg" title={tooltipText} />
    </>
  );
}

type Props = {
  form: FormInstance;
  setRatesValues: (formData: UnitRateType) => void;
  effectiveDate: string;
  setEffectiveDate: Dispatch<SetStateAction<string>>;
  setEndDate: Dispatch<SetStateAction<string>>;
  pricingMode: 'DYNAMIC' | 'STATIC';
};

export function IncentivesUpdateRatesSetupStep({
  form,
  setRatesValues,
  effectiveDate,
  setEffectiveDate,
  setEndDate,
  pricingMode,
}: Props) {
  const { loading, error, data } = useQuery(OrganizationSettingQuery);
  const isStaticPricing = pricingMode === 'STATIC';

  const { defaultIncentiveIncrement, allowCustomIncentiveIncrements } =
    data?.organizationSetting || {};

  const [customIncentiveIncrement, setCustomIncentiveIncrement] = useState(0);

  useEffect(() => {
    setCustomIncentiveIncrement(defaultIncentiveIncrement || 0);
  }, [defaultIncentiveIncrement]);

  useEffect(() => {
    if (!isStaticPricing) return;

    const tiers = form.getFieldValue('staticPricingTiers') as
      | StaticPricingTierFormValue[]
      | undefined;
    if (!tiers || tiers.length === 0) {
      form.setFieldsValue({ staticPricingTiers: DEFAULT_STATIC_PRICING_TIERS });
    }
  }, [form, isStaticPricing]);

  if (loading) {
    return <LoadingState />;
  }

  if (error) {
    return <ErrorHandler error={error} log="on-demand-rates-step-three" />;
  }

  function onFormFinish(formRawData: UnitRateType) {
    const filteredData = filterFormData(formRawData, pricingMode);

    setEffectiveDate(formRawData.effectiveDate);
    setEndDate(formRawData.endDate || '');

    setRatesValues(filteredData);
  }

  return (
    <>
      <Form
        className="rates-on-demand-step-three"
        layout="vertical"
        onFinish={onFormFinish}
        form={form}
        initialValues={{
          incentiveIncrement: defaultIncentiveIncrement,
          ...STATIC_INITIAL_VALUES,
        }}
      >
        <section>
          <Gutter direction="bottom">
            <Title size="h3">Effective Dates</Title>
          </Gutter>
          <Row gutter={24}>
            <Col span={4}>
              <Form.Item
                rules={[
                  {
                    required: true,
                    message: 'Please select an Effective Date',
                  },
                ]}
                name="effectiveDate"
                label="Start Date"
              >
                <DatePicker
                  style={{
                    width: '100%',
                  }}
                  format="MM/DD/YYYY"
                  data-testid="effective-date"
                  disabledDate={isPastDay}
                  onChange={(date) => {
                    setEffectiveDate(date?.toISOString() || '');
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item
                name="endDate"
                label={
                  <LabelWithTooltip
                    label="End Date"
                    tooltipText="If an end date is specified, incentive rates will return to the previously active rate card"
                  />
                }
              >
                <DatePicker
                  style={{
                    width: '100%',
                  }}
                  format="MM/DD/YYYY"
                  data-testid="end-date"
                  disabled={!effectiveDate}
                  defaultPickerValue={datetime(effectiveDate)}
                  disabledDate={(date) => date.isBefore(effectiveDate)}
                  placeholder="None"
                />
              </Form.Item>
            </Col>
          </Row>
          {isStaticPricing ? (
            <>
              <StaticPricingTiersFormList form={form} />
              <Form.Item name="displayAsLumpSum" valuePropName="checked">
                <Checkbox>Display incentives as total shift pay</Checkbox>
              </Form.Item>
              <Text color="steelDarkest">
                When enabled, incentives will be displayed to managers and
                workers as the total shift payout instead of an hourly rate.
              </Text>
            </>
          ) : (
            <>
              <Gutter direction="bottom">
                <Title size="h3">Dynamic Pricing Settings</Title>
              </Gutter>
              <Row gutter={24}>
                <Col span={4}>
                  <RateCurrencyInput
                    name={priceRange}
                    increment={customIncentiveIncrement}
                    validateIncrement
                    label={
                      <LabelWithTooltip
                        label="Price Range"
                        tooltipText="Price Range determines how much prices can be adjusted down from the maximum incentive rate"
                      />
                    }
                  />
                </Col>
                <Col span={4}>
                  <RateCurrencyInput
                    name={incentiveIncrement}
                    validateIncrement
                    label={
                      <LabelWithTooltip
                        label="Price Increment"
                        tooltipText="Price Increment determines how many individual rate offerings are available. For example, $10 range and $1 increment would generate 10 unique incentive rate options"
                      />
                    }
                    onChange={(value) =>
                      setCustomIncentiveIncrement(value as number)
                    }
                    disabled={!allowCustomIncentiveIncrements}
                  />
                </Col>
              </Row>
            </>
          )}
        </section>
        {!isStaticPricing && (
          <>
            <Divider />
            <section>
              <Title size="h3">Incentive Rates</Title>
              <Row gutter={24}>
                <Col span={24}>
                  <VariableRatesInputsTable
                    increment={customIncentiveIncrement}
                    setFields={form.setFieldsValue}
                    cleanFields={form.resetFields}
                  />
                </Col>
              </Row>
            </section>
          </>
        )}
      </Form>
      <style jsx>{`
        :global(.rates-on-demand-step-three__default-title) {
          font-size: ${FontSize.lg};
          line-height: ${LineHeight.lg};
        }
        :global(.rates-on-demand-step-three__default-title) {
          margin-bottom: 16px;
        }
        :global(.rates-on-demand-step-three .ant-divider) {
          margin: 24px 0;
        }
        .inputs-column-label {
          line-height: ${LineHeight.lg};
        }
      `}</style>
    </>
  );
}
